import React from 'react'
import type { UserMembership, MembershipTier } from '../../types/membership'
import type { MembershipThresholds } from '../../services/membershipService'

interface MembershipProgressProps {
  membership: UserMembership
  thresholds?: MembershipThresholds | null
}

const tierOrder: MembershipTier[] = ['none', 'bronze', 'silver', 'gold', 'platinum', 'vip']

const defaultTierThresholds: Record<MembershipTier, number> = {
  none: 0,
  bronze: 1,
  silver: 5,
  gold: 10,
  platinum: 20,
  vip: 50,
}

const nextTierLabel: Record<MembershipTier, string | null> = {
  none: 'Bronze',
  bronze: 'Silver',
  silver: 'Gold',
  gold: 'Platinum',
  platinum: 'VIP',
  vip: null,
}

const nextTierColor: Record<MembershipTier, string> = {
  none: 'bg-amber-500',
  bronze: 'bg-gray-400',
  silver: 'bg-yellow-500',
  gold: 'bg-gray-300',
  platinum: 'bg-purple-500',
  vip: 'bg-purple-500',
}

function getThresholds(thresholds?: MembershipThresholds | null): Record<MembershipTier, number> {
  if (!thresholds) return defaultTierThresholds
  return {
    none: 0,
    bronze: thresholds.membership_bronze_threshold ?? defaultTierThresholds.bronze,
    silver: thresholds.membership_silver_threshold ?? defaultTierThresholds.silver,
    gold: thresholds.membership_gold_threshold ?? defaultTierThresholds.gold,
    platinum: thresholds.membership_platinum_threshold ?? defaultTierThresholds.platinum,
    vip: thresholds.membership_vip_threshold ?? defaultTierThresholds.vip,
  }
}

export const MembershipProgress: React.FC<MembershipProgressProps> = ({ membership, thresholds }) => {
  const currentIndex = tierOrder.indexOf(membership.tier)
  const isMaxTier = membership.tier === 'vip'
  const tierThresholds = getThresholds(thresholds)

  if (isMaxTier) {
    return (
      <div className="text-center py-3">
        <p className="text-sm font-bold text-purple-600 dark:text-purple-400">Tier Tertinggi!</p>
      </div>
    )
  }

  const nextTier = tierOrder[currentIndex + 1]
  const currentThreshold = tierThresholds[membership.tier]
  const nextThreshold = tierThresholds[nextTier]
  const needed = nextThreshold - membership.totalAttendance
  const progress = Math.min(
    ((membership.totalAttendance - currentThreshold) / (nextThreshold - currentThreshold)) * 100,
    100,
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold text-slate-500 dark:text-slate-400">
          {membership.totalAttendance} total hadir
        </span>
        <span className="font-bold text-slate-700 dark:text-slate-300">
          {needed} lagi ke {nextTierLabel[membership.tier]}
        </span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${nextTierColor[membership.tier]}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}
